// The Gantt chart's time axis: which days it shows, how wide a day is, and
// the two rows of labels along the top.
import { TaskItem } from '../../../types/tasks';
import { addDays, addMonths, daysBetween, mondayOf, parseDay } from '../../../utils/taskDates';

export type GanttZoom = 'day' | 'week' | 'month';

export const PX_PER_DAY: Record<GanttZoom, number> = { day: 34, week: 14, month: 4 };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * From the Monday a week before the earliest bar (or today) to two months
 * past the last — and always at least a year ahead, so next season can be
 * planned (dragged out) before anything is on it.
 */
export function ganttRange(items: TaskItem[], today: string): { from: string; to: string } {
  let first = today;
  let last = today;
  for (const i of items) {
    if (i.plan_start && i.plan_start < first) first = i.plan_start;
    if (i.plan_finish && i.plan_finish > last) last = i.plan_finish;
    if (i.due_date && i.due_date > last) last = i.due_date;
  }
  const from = mondayOf(addDays(first, -7));
  const end = [addDays(last, 60), addDays(today, 365)].sort().pop()!;
  return { from, to: addDays(mondayOf(end), 6) };
}

export interface Tick {
  key: string;
  label: string;
  x: number;
  w: number;
}

/** Top row (months, or years zoomed out) and bottom row (days, weeks or months). */
export function ganttTicks(from: string, to: string, zoom: GanttZoom): { top: Tick[]; bottom: Tick[] } {
  const px = PX_PER_DAY[zoom];
  const x = (day: string) => daysBetween(from, day) * px;
  const total = daysBetween(from, to) + 1;
  const top: Tick[] = [];
  const bottom: Tick[] = [];

  // Months (top for day/week, bottom for month).
  const months: Tick[] = [];
  for (let d = `${from.slice(0, 7)}-01`; d <= to; ) {
    const next = addMonths(d, 1);
    const start = d < from ? from : d;
    const end = next > to ? addDays(to, 1) : next;
    const p = parseDay(d);
    months.push({ key: d, label: zoom === 'month' ? MONTHS[p.getMonth()] : `${MONTHS[p.getMonth()]} ${p.getFullYear()}`, x: x(start), w: daysBetween(start, end) * px });
    d = next;
  }

  if (zoom === 'month') {
    bottom.push(...months);
    const years = new Map<string, Tick>();
    months.forEach((m) => {
      const y = m.key.slice(0, 4);
      const t = years.get(y);
      if (t) t.w += m.w;
      else years.set(y, { key: y, label: y, x: m.x, w: m.w });
    });
    top.push(...Array.from(years.values()));
  } else {
    top.push(...months);
    const step = zoom === 'day' ? 1 : 7;
    for (let i = 0; i < total; i += step) {
      const d = addDays(from, i);
      bottom.push({ key: d, label: String(parseDay(d).getDate()), x: i * px, w: step * px });
    }
  }
  return { top, bottom };
}
